/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useState, useCallback } from 'react'
import i18next from 'i18next'
import { toast } from 'sonner'
import { requestWechatPayment, isApiSuccess } from '../api'
import type { WechatPaymentData } from '../types'

/**
 * Hook for handling WeChat Native payment processing.
 * Creates a WeChat Pay order and returns the code_url for QR display.
 */
export function useWechatPayment() {
  const [processing, setProcessing] = useState(false)
  const [paymentData, setPaymentData] = useState<WechatPaymentData | null>(null)

  const processWechatPayment = useCallback(
    async (topupAmount: number): Promise<WechatPaymentData | null> => {
      setProcessing(true)
      setPaymentData(null)

      try {
        const response = await requestWechatPayment({
          amount: Math.floor(topupAmount),
          payment_method: 'wechat',
        })

        if (isApiSuccess(response) && response.data) {
          const data = response.data as WechatPaymentData
          setPaymentData(data)
          return data
        }

        toast.error(
          (typeof response.message === 'string' && response.message) ||
            i18next.t('Payment request failed')
        )
        return null
      } catch (_error) {
        toast.error(i18next.t('Payment request failed'))
        return null
      } finally {
        setProcessing(false)
      }
    },
    []
  )

  const clearPaymentData = useCallback(() => {
    setPaymentData(null)
  }, [])

  return {
    processing,
    paymentData,
    processWechatPayment,
    clearPaymentData,
  }
}
